/**
 * EntityEditor Component
 *
 * Generic reusable component for editing lookup tables:
 * - Table listing all items
 * - Add new item button
 * - Edit/delete actions per row
 * - Primary key is immutable (readonly)
 */

import { Button, Input, Alert, Spinner, useDialog } from '@medsuite/ui-components';
import { useState, useEffect } from 'react';

interface Column {
  key: string;
  label: string;
  type?: 'text' | 'number' | 'select';
  options?: Array<{ value: string | number; label: string }>;
  readonly?: boolean;
}

interface EntityEditorProps<T extends Record<string, unknown>> {
  title: string;
  columns: Column[];
  data: T[];
  isLoading: boolean;
  error?: Error | null;
  primaryKey: string;
  onAdd?: (item: Partial<T>) => Promise<void>;
  onUpdate?: (id: string | number, item: Partial<T>) => Promise<void>;
  onDelete?: (id: string | number) => Promise<void>;
  isAdding?: boolean;
  isUpdating?: boolean;
  isDeleting?: boolean;
  onDataLoaded?: (count: number) => void;
}

export default function EntityEditor<T extends Record<string, unknown>>({
  title,
  columns,
  data,
  isLoading,
  error,
  primaryKey,
  onAdd,
  onUpdate,
  onDelete,
  isAdding = false,
  isUpdating = false,
  isDeleting = false,
  onDataLoaded,
}: EntityEditorProps<T>) {
  // Dialog hooks
  const { confirm } = useDialog();

  // Local state
  const [isAddingNew, setIsAddingNew] = useState(false);
  const [newItem, setNewItem] = useState<Partial<T>>({});
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [editingItem, setEditingItem] = useState<Partial<T>>({});

  // Report data count to parent
  useEffect(() => {
    if (!isLoading && onDataLoaded) {
      onDataLoaded(data.length);
    }
  }, [data.length, isLoading, onDataLoaded]);

  // Handle add new item
  const handleStartAdd = () => {
    setIsAddingNew(true);
    setNewItem({});
  };

  const handleCancelAdd = () => {
    setIsAddingNew(false);
    setNewItem({});
  };

  const handleSaveAdd = async () => {
    if (!onAdd) {
      return;
    }
    try {
      await onAdd(newItem);
      setIsAddingNew(false);
      setNewItem({});
    } catch (error) {
      console.error('Failed to add item:', error);
    }
  };

  // Handle edit item
  const handleStartEdit = (item: T) => {
    setEditingId(item[primaryKey] as string | number);
    setEditingItem({ ...item });
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditingItem({});
  };

  const handleSaveEdit = async () => {
    if (!onUpdate) {
      return;
    }
    try {
      await onUpdate(editingId!, editingItem);
      setEditingId(null);
      setEditingItem({});
    } catch (error) {
      console.error('Failed to update item:', error);
    }
  };

  // Handle delete item
  const handleDelete = async (id: string | number) => {
    if (!onDelete) {
      return;
    }
    const confirmed = await confirm({
      title: 'Delete Item',
      message: 'Are you sure you want to delete this item?',
      variant: 'danger',
      confirmText: 'Delete',
    });
    if (!confirmed) {
      return;
    }

    try {
      await onDelete(id);
    } catch (error) {
      console.error('Failed to delete item:', error);
    }
  };

  // Handle field change
  const handleFieldChange = (target: 'new' | 'edit', key: string, value: string | number) => {
    if (target === 'new') {
      setNewItem((prev) => ({ ...prev, [key]: value }));
    } else {
      setEditingItem((prev) => ({ ...prev, [key]: value }));
    }
  };

  // Render field input
  const renderInput = (
    column: Column,
    value: unknown,
    onChange: (value: string | number) => void,
    readonly: boolean = false
  ) => {
    if (readonly) {
      return (
        <Input
          type="text"
          value={String(value || '')}
          readOnly
          className="bg-gray-50 dark:bg-gray-700 cursor-not-allowed"
        />
      );
    }

    if (column.type === 'select' && column.options) {
      // Check if options have numeric values to convert on change
      const hasNumericValues =
        column.options.length > 0 && typeof column.options[0].value === 'number';
      return (
        <select
          value={String(value ?? '')}
          onChange={(e) => {
            const val = e.target.value;
            // Convert to number if options have numeric values
            onChange(val === '' ? '' : hasNumericValues ? Number(val) : val);
          }}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:focus:ring-indigo-400"
        >
          <option value="">Select...</option>
          {column.options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    if (column.type === 'number') {
      return (
        <Input
          type="number"
          value={value != null ? String(value) : ''}
          onChange={(e) => {
            const val = e.target.value;
            onChange(val === '' ? '' : Number(val));
          }}
        />
      );
    }

    return <Input type="text" value={String(value || '')} onChange={(e) => onChange(e.target.value)} />;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="error">
        Failed to load {title.toLowerCase()}: {error.message}
      </Alert>
    );
  }

  return (
    <div className="space-y-4">
      {/* Add Button */}
      <div className="flex items-center justify-end">
        {!isAddingNew && (
          <Button variant="primary" onClick={handleStartAdd}>
            Add New
          </Button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              {columns.map((column) => (
                <th
                  key={column.key}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                >
                  {column.label}
                </th>
              ))}
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {/* Add New Row */}
            {isAddingNew && (
              <tr className="bg-blue-50 dark:bg-blue-950">
                {columns.map((column) => (
                  <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                    {column.key === primaryKey ? (
                      <span className="text-sm text-gray-500 dark:text-gray-400 italic">
                        Auto-generated
                      </span>
                    ) : (
                      renderInput(column, newItem[column.key], (value) =>
                        handleFieldChange('new', column.key, value)
                      )
                    )}
                  </td>
                ))}
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={handleCancelAdd}
                      className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                      disabled={isAdding}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSaveAdd}
                      className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                      disabled={isAdding}
                    >
                      {isAdding ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </td>
              </tr>
            )}

            {/* Data Rows */}
            {data.length === 0 && !isAddingNew ? (
              <tr>
                <td
                  colSpan={columns.length + 1}
                  className="px-6 py-12 text-center text-gray-500 dark:text-gray-400"
                >
                  No items found. Click &quot;Add New&quot; to create one.
                </td>
              </tr>
            ) : (
              data.map((item) => {
                const isEditing = editingId === item[primaryKey];

                return (
                  <tr
                    key={item[primaryKey] as string | number}
                    className={isEditing ? 'bg-yellow-50 dark:bg-yellow-950' : ''}
                  >
                    {columns.map((column) => (
                      <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                        {isEditing ? (
                          renderInput(
                            column,
                            editingItem[column.key],
                            (value) => handleFieldChange('edit', column.key, value),
                            column.key === primaryKey || column.readonly
                          )
                        ) : (
                          <div className="text-sm text-gray-900 dark:text-gray-100">
                            {column.type === 'select' && column.options
                              ? column.options.find((opt) => opt.value === item[column.key])
                                  ?.label ||
                                item[column.key]?.toString() ||
                                '-'
                              : item[column.key]?.toString() || '-'}
                          </div>
                        )}
                      </td>
                    ))}
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      {isEditing ? (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={handleCancelEdit}
                            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
                            disabled={isUpdating}
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                            disabled={isUpdating}
                          >
                            {isUpdating ? 'Saving...' : 'Save'}
                          </button>
                        </div>
                      ) : (
                        <div className="flex justify-end gap-2">
                          <button
                            onClick={() => handleStartEdit(item)}
                            className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-900 dark:hover:text-indigo-300"
                            disabled={editingId !== null || isAddingNew}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item[primaryKey] as string | number)}
                            className="text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300"
                            disabled={editingId !== null || isAddingNew || isDeleting}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
